/**
 * The node picker: one searchable dialog for everything that can be added.
 *
 * Browse by category — Triggers, Apps, Flow, Data, HTTP, Output — or type
 * to search across all of it at once ("gmail unread" finds Gmail › List
 * messages). An app is a family of nodes: opening it lists what it can watch
 * (its list actions, as app events) and what it can do (its actions). Picking
 * an action asks which connection to use when there is more than one, and
 * skips the question when there is one.
 *
 * Every path ends in `onPick` with a ready-to-add node: its type, params and
 * a good name.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { useConnections } from '@/core/connection/connection.context'
import type { Connection } from '@/core/types/api.types'
import { Logo } from '@/shared/components'
import { Icon } from '@/shared/components/icons'
import { NodeIcon } from '../canvas/icons'
import type { AppAction, AppSpec, Catalogue, NodeSpec } from '../types'

export interface Pick {
  type: string
  name: string
  params: Record<string, unknown>
  app?: AppSpec
}

interface Props {
  catalogue: Catalogue
  /** No trigger yet: open on Triggers, and offer app events first. */
  needsTrigger: boolean
  /** Adding after a port: triggers cannot be wired to, so they are hidden. */
  afterPort: boolean
  onPick: (pick: Pick) => void
  onClose: () => void
}

type Category = 'trigger' | 'apps' | 'brain' | 'flow' | 'data' | 'http' | 'output'

const CATEGORIES: { key: Category; label: string; hint: string }[] = [
  { key: 'trigger', label: 'Triggers', hint: 'Where a run starts' },
  { key: 'apps', label: 'Apps', hint: 'Your connected apps' },
  { key: 'brain', label: 'Knowledge graph', hint: 'Read and build the graph' },
  { key: 'flow', label: 'Flow', hint: 'Branch, loop, wait' },
  { key: 'data', label: 'Data', hint: 'Shape the items' },
  { key: 'http', label: 'HTTP', hint: 'Any URL' },
  { key: 'output', label: 'Output', hint: 'Answer and log' },
]

/** A row in search results or a category list; each one becomes a node. */
interface Row {
  id: string
  kind: 'node' | 'app' | 'action' | 'event' | 'apps'
  title: string
  subtitle: string
  icon: React.ReactNode
  group: string
  score: number
  spec?: NodeSpec
  app?: AppSpec
  action?: AppAction
}

export function NodePicker({ catalogue, needsTrigger, afterPort, onPick, onClose }: Props) {
  const { byConnector } = useConnections()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<Category>(needsTrigger ? 'trigger' : afterPort ? 'apps' : 'trigger')
  const [app, setApp] = useState<AppSpec | null>(null)
  const [pending, setPending] = useState<{ app: AppSpec; action: AppAction; asEvent: boolean } | null>(null)
  const [cursor, setCursor] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => input.current?.focus(), [app, pending])
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const showTriggers = !afterPort
  const specs = useMemo(() => catalogue.nodes.filter((n) => n.type !== 'connector.action' && n.type !== 'trigger.app'), [catalogue.nodes])

  // --- rows ----------------------------------------------------------------------------

  const nodeRow = (spec: NodeSpec, score = 0): Row => ({
    id: `node:${spec.type}`,
    kind: 'node',
    title: spec.label,
    subtitle: spec.summary,
    icon: <span className="wf-node-icon" data-group={spec.group}><NodeIcon name={spec.icon} /></span>,
    group: spec.group,
    score,
    spec,
  })
  const appRow = (a: AppSpec, score = 0): Row => ({
    id: `app:${a.id}`,
    kind: 'app',
    title: a.name,
    subtitle: `${a.actions.length} action${a.actions.length === 1 ? '' : 's'}${(byConnector[a.id]?.length ?? 0) > 0 ? ' · connected' : ''}`,
    icon: <Logo src={a.logo} name={a.name} id={a.id} small />,
    group: 'connector',
    score,
    app: a,
  })
  const actionRow = (a: AppSpec, act: AppAction, asEvent: boolean, score = 0): Row => ({
    id: `${asEvent ? 'event' : 'action'}:${a.id}:${act.id}`,
    kind: asEvent ? 'event' : 'action',
    title: asEvent ? `New ${act.name.toLowerCase().replace(/^(list|search|get)\s+/i, '')}` : act.name,
    subtitle: asEvent ? `${a.name} · runs when ${act.name} returns something new` : `${a.name} · ${act.description || act.category || 'action'}`,
    icon: <Logo src={a.logo} name={a.name} id={a.id} small />,
    group: asEvent ? 'trigger' : 'connector',
    score,
    app: a,
    action: act,
  })

  const needle = query.trim().toLowerCase()
  const rows: Row[] = useMemo(() => {
    if (needle) {
      const words = needle.split(/\s+/)
      const scoreOf = (...texts: string[]) => {
        const hay = texts.join(' ').toLowerCase()
        let score = 0
        for (const w of words) {
          if (!hay.includes(w)) return -1
          score += hay.startsWith(w) ? 3 : 1
        }
        return score
      }
      const out: Row[] = []
      for (const s of specs) {
        if (!showTriggers && s.group === 'trigger') continue
        const sc = scoreOf(s.label, s.summary, s.type)
        if (sc >= 0) out.push(nodeRow(s, sc + 1))
      }
      for (const a of catalogue.apps) {
        const appScore = scoreOf(a.name, a.description, a.category)
        if (appScore >= 0) out.push(appRow(a, appScore + 2))
        for (const act of a.actions) {
          const sc = scoreOf(a.name, act.name, act.description, act.category ?? '')
          if (sc < 0) continue
          out.push(actionRow(a, act, false, sc))
          if (showTriggers && act.watchable) out.push(actionRow(a, act, true, sc - 0.5))
        }
      }
      return out.sort((x, y) => y.score - x.score).slice(0, 60)
    }
    if (app) {
      const out: Row[] = []
      if (showTriggers) for (const act of app.actions) if (act.watchable) out.push(actionRow(app, act, true))
      for (const act of app.actions) out.push(actionRow(app, act, false))
      return out
    }
    if (category === 'apps') {
      const connected = catalogue.apps.filter((a) => (byConnector[a.id]?.length ?? 0) > 0)
      const rest = catalogue.apps.filter((a) => !(byConnector[a.id]?.length ?? 0))
      return [...connected, ...rest].map((a) => appRow(a))
    }
    const list = specs.filter((s) => s.group === category).map((s) => nodeRow(s))
    if (category === 'trigger') {
      list.splice(1, 0, {
        id: 'apps:events',
        kind: 'apps',
        title: 'App event',
        subtitle: 'When something new appears in a connected app — a mail, a row, a file',
        icon: <span className="wf-node-icon" data-group="trigger"><NodeIcon name="bolt" /></span>,
        group: 'trigger',
        score: 0,
      })
    }
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle, app, category, specs, catalogue.apps, byConnector, showTriggers])

  useEffect(() => setCursor(0), [needle, app, category])
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${cursor}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  // --- choosing --------------------------------------------------------------------------

  function choose(row: Row) {
    if (row.kind === 'apps') {
      setCategory('apps')
      return
    }
    if (row.kind === 'app' && row.app) {
      setApp(row.app)
      setQuery('')
      return
    }
    if (row.kind === 'node' && row.spec) {
      const params: Record<string, unknown> = {}
      for (const p of row.spec.params) if (p.default !== null && p.default !== undefined) params[p.key] = p.default
      onPick({ type: row.spec.type, name: row.spec.label, params })
      return
    }
    if ((row.kind === 'action' || row.kind === 'event') && row.app && row.action) {
      const connections = byConnector[row.app.id] ?? []
      if (connections.length === 1) return finish(row.app, row.action, row.kind === 'event', connections[0])
      setPending({ app: row.app, action: row.action, asEvent: row.kind === 'event' })
    }
  }

  function finish(a: AppSpec, act: AppAction, asEvent: boolean, connection: Connection | null) {
    const base = { connector_id: a.id, connection_id: connection?.connection_id ?? '', action_id: act.id, input: {} }
    if (asEvent) {
      onPick({
        type: 'trigger.app',
        name: `New ${act.name.toLowerCase().replace(/^(list|search|get)\s+/i, '')}`,
        params: { ...base, minutes: 5, key: 'id', first_poll: 'skip' },
        app: a,
      })
    } else {
      onPick({ type: 'connector.action', name: `${a.name}: ${act.name}`, params: { ...base, mode: 'once' }, app: a })
    }
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => Math.min(rows.length - 1, c + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => Math.max(0, c - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const row = rows[cursor]
      if (row) choose(row)
    } else if (e.key === 'Backspace' && !query && app) {
      setApp(null)
    }
  }

  // --- render ----------------------------------------------------------------------------

  if (pending) {
    const connections = byConnector[pending.app.id] ?? []
    return (
      <div className="wf-picker-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Choose a connection">
        <div className="wf-picker wf-picker-narrow">
          <header className="wf-picker-head">
            <button type="button" className="wf-picker-back" onClick={() => setPending(null)} aria-label="Back"><Icon.chevronLeft /></button>
            <Logo src={pending.app.logo} name={pending.app.name} id={pending.app.id} small />
            <div className="wf-picker-title">
              <strong>{pending.action.name}</strong>
              <span className="muted small">{pending.app.name} · which account?</span>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">✕</button>
          </header>
          <div className="wf-picker-body wf-picker-connections">
            {connections.map((c) => (
              <button key={c.connection_id} type="button" className="wf-picker-row" onClick={() => finish(pending.app, pending.action, pending.asEvent, c)}>
                <span className="wf-node-icon" data-group="connector"><Icon.plug /></span>
                <span className="wf-picker-text">
                  <span className="wf-picker-label">{c.label}</span>
                  <span className="wf-picker-sub">{c.auth_method_label ?? c.auth_type}{c.status !== 'active' ? ` · ${c.status}` : ''}</span>
                </span>
              </button>
            ))}
            {connections.length === 0 && (
              <div className="wf-picker-empty">
                <strong>{pending.app.name} is not connected yet</strong>
                <p className="muted small">Connect it first, or add the step now and choose a connection later.</p>
                <div className="row">
                  <Link className="btn btn-primary btn-sm" to={`/connectors/${encodeURIComponent(pending.app.id)}`}>Connect {pending.app.name}</Link>
                  <button type="button" className="btn btn-sm" onClick={() => finish(pending.app, pending.action, pending.asEvent, null)}>Add anyway</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="wf-picker-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Add a node">
      <div className="wf-picker">
        <header className="wf-picker-head">
          {app ? (
            <>
              <button type="button" className="wf-picker-back" onClick={() => { setApp(null); setQuery('') }} aria-label="Back to all apps"><Icon.chevronLeft /></button>
              <Logo src={app.logo} name={app.name} id={app.id} small />
            </>
          ) : (
            <Icon.search className="wf-picker-search-icon" />
          )}
          <input
            ref={input}
            className="wf-picker-input"
            value={query}
            placeholder={app ? `Search ${app.name} actions` : needsTrigger ? 'What should start this workflow?' : 'Search apps, actions, triggers, flow…'}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            aria-label="Search nodes"
          />
          <kbd className="wf-kbd">esc</kbd>
        </header>

        <div className="wf-picker-body">
          {!needle && !app && (
            <nav className="wf-picker-nav" aria-label="Categories">
              {CATEGORIES.filter((c) => showTriggers || c.key !== 'trigger').map((c) => (
                <button key={c.key} type="button" data-on={category === c.key || undefined} onClick={() => setCategory(c.key)}>
                  <span>{c.label}</span>
                  <span className="wf-picker-nav-hint">{c.hint}</span>
                </button>
              ))}
            </nav>
          )}

          <div className="wf-picker-list" ref={listRef} role="listbox">
            {app && !needle && (
              <div className="wf-picker-appintro">
                <strong>{app.name}</strong>
                <span className="muted small">{app.description}</span>
                {(byConnector[app.id]?.length ?? 0) === 0 && (
                  <Link className="small" to={`/connectors/${encodeURIComponent(app.id)}`}>Not connected yet — connect {app.name}</Link>
                )}
              </div>
            )}
            {grouped(rows, Boolean(needle), Boolean(app), category).map((section) => (
              <section key={section.title} className="wf-picker-section">
                {section.title && <h4 className="wf-picker-section-title">{section.title}</h4>}
                {section.rows.map((row) => {
                  const index = rows.indexOf(row)
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className="wf-picker-row"
                      data-index={index}
                      data-on={index === cursor || undefined}
                      role="option"
                      aria-selected={index === cursor}
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => choose(row)}
                    >
                      {row.icon}
                      <span className="wf-picker-text">
                        <span className="wf-picker-label">{row.title}</span>
                        <span className="wf-picker-sub">{row.subtitle}</span>
                      </span>
                      {row.kind === 'app' || row.kind === 'apps' ? <Icon.chevronLeft className="wf-picker-chevron" /> : <span className="wf-picker-add" aria-hidden="true">+</span>}
                    </button>
                  )
                })}
              </section>
            ))}
            {rows.length === 0 && (
              <div className="wf-picker-empty">
                <strong>Nothing matches “{query}”</strong>
                <p className="muted small">Try an app name, an action, or a word like “branch”, “loop”, “webhook”.</p>
              </div>
            )}
          </div>
        </div>

        <footer className="wf-picker-foot small muted">
          <span><kbd className="wf-kbd">↑</kbd><kbd className="wf-kbd">↓</kbd> move</span>
          <span><kbd className="wf-kbd">↵</kbd> add</span>
          {app && <span><kbd className="wf-kbd">⌫</kbd> back</span>}
        </footer>
      </div>
    </div>
  )
}

function grouped(rows: Row[], searching: boolean, inApp: boolean, category: Category): { title: string; rows: Row[] }[] {
  if (inApp && !searching) {
    const events = rows.filter((r) => r.kind === 'event')
    const actions = rows.filter((r) => r.kind === 'action')
    return [
      ...(events.length ? [{ title: 'Start when something is new', rows: events }] : []),
      { title: events.length ? 'Actions' : '', rows: actions },
    ]
  }
  if (searching) {
    const kinds: [Row['kind'], string][] = [['event', 'App events'], ['app', 'Apps'], ['action', 'Actions'], ['node', 'Steps']]
    return kinds.map(([k, title]) => ({ title, rows: rows.filter((r) => r.kind === k) })).filter((s) => s.rows.length)
  }
  if (category === 'apps') {
    const connected = rows.filter((r) => r.subtitle.includes('connected'))
    const rest = rows.filter((r) => !r.subtitle.includes('connected'))
    return [
      ...(connected.length ? [{ title: 'Connected', rows: connected }] : []),
      { title: connected.length ? 'All apps' : '', rows: rest },
    ]
  }
  return [{ title: '', rows }]
}
